import { cn } from '@/lib/utils';
import { Button, toast, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Volume2 } from 'lucide-react';
import type {
  ChallengeEditorContext,
  ChallengeEditorSectionProps,
} from '../types';
import { SectionCard } from './SectionCard';

interface VoiceoverSectionProps
  extends Pick<ChallengeEditorSectionProps, 'values' | 'updateField'> {
  context: ChallengeEditorContext;
  challengeId: string;
  /** Required in event context. */
  eventId?: string;
}

/**
 * AI voiceover of the clue: player + generate/regenerate. Edit mode only —
 * both the library (`POST /challenges/:id/regenerate-audio`) and event
 * (`POST /events/:id/challenges/:challengeId/regenerate-audio`) endpoints
 * respond with `{ data: { audioUrl } }`, which updates the player in place.
 */
export function VoiceoverSection({
  values,
  updateField,
  context,
  challengeId,
  eventId,
}: VoiceoverSectionProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const regenerateUrl =
    context === 'event'
      ? `/events/${eventId}/challenges/${challengeId}/regenerate-audio`
      : `/challenges/${challengeId}/regenerate-audio`;

  const regenerateMutation = useMutation({
    mutationFn: async () =>
      apiClient.post<{ success: boolean; data: { audioUrl: string } }>(regenerateUrl),
    onSuccess: (response) => {
      if (response.data?.audioUrl) updateField('audioUrl', response.data.audioUrl);
      if (context === 'event') {
        queryClient.invalidateQueries({ queryKey: ['EventChallenge', eventId, challengeId] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['Challenge', challengeId] });
      }
    },
  });

  const handleRegenerate = () => {
    toast.promise(regenerateMutation.mutateAsync(), {
      loading: 'Generating audio voiceover...',
      success: 'Audio voiceover regenerated',
      error: 'Failed to regenerate audio',
    });
  };

  return (
    <SectionCard
      id="voiceover"
      title="Audio Voiceover"
      description="AI-generated audio of the clue text"
      action={
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleRegenerate}
          disabled={regenerateMutation.isPending}
        >
          <RefreshCw
            className={cn('h-4 w-4', regenerateMutation.isPending && 'animate-spin')}
          />
          {values.audioUrl ? 'Regenerate' : 'Generate'}
        </Button>
      }
    >
      {values.audioUrl ? (
        <div className="flex items-center gap-3">
          <Volume2 className="h-5 w-5 text-muted-foreground shrink-0" />
          <audio controls src={values.audioUrl} className="w-full" />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground italic">
          No audio voiceover generated yet. Click &quot;Generate&quot; to create one.
        </p>
      )}
    </SectionCard>
  );
}
